/**
 * React Query Configuration
 * Centralized configuration for React Query with proper cache settings
 * Implements optimistic updates and stale-while-revalidate patterns
 */

import { QueryClient } from '@tanstack/react-query';
import config from '../config';
import { notificationService } from '../services/notificationService';

// Query configuration constants from config
const QUERY_CONFIG = {
  STALE_TIME: 5 * 60 * 1000, // 5 minutes
  CACHE_TIME: 10 * 60 * 1000, // 10 minutes
  RETRY_ATTEMPTS: config.API.MAX_RETRY_ATTEMPTS,
  RETRY_DELAY: config.API.RETRY_DELAY,
  REFETCH_ON_WINDOW_FOCUS: !config.isDevelopment(),
} as const;

/**
 * Create and configure React Query client
 */
export const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Cache configuration
        staleTime: QUERY_CONFIG.STALE_TIME,
        gcTime: QUERY_CONFIG.CACHE_TIME, // Previously cacheTime
        
        // Retry configuration
        retry: QUERY_CONFIG.RETRY_ATTEMPTS,
        retryDelay: (attemptIndex) => 
          Math.min(QUERY_CONFIG.RETRY_DELAY * Math.pow(2, attemptIndex), 30000),
        
        // Refetch configuration
        refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_WINDOW_FOCUS,
        refetchOnReconnect: true,
        refetchOnMount: true,
        
        // Error handling
        throwOnError: false,
      },
      mutations: {
        // Retry configuration for mutations
        retry: (failureCount, error: any) => {
          // Don't retry on client errors (4xx)
          if (error?.status >= 400 && error?.status < 500) {
            return false;
          }
          // Retry up to 2 times for server errors
          return failureCount < 2;
        },
        
        // Global error handling for mutations
        onError: (error: any) => {
          // Handle different types of errors
          if (error?.isNetworkError) {
            notificationService.networkError();
          } else if (error?.status === 401) {
            notificationService.error('Authentication required. Please log in.');
          } else if (error?.status === 403) {
            notificationService.permissionDenied();
          } else if (error?.status === 429) {
            notificationService.rateLimited();
          } else {
            notificationService.apiError(error);
          }
        },
      },
    },
  });
};

/**
 * Query key factory for consistent key generation
 */
export const queryKeys = {
  // Authentication
  auth: {
    profile: () => ['auth', 'profile'] as const,
  },
  
  // Prompts
  prompts: {
    all: () => ['prompts'] as const,
    lists: () => [...queryKeys.prompts.all(), 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.prompts.lists(), params] as const,
    details: () => [...queryKeys.prompts.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.prompts.details(), id] as const,
    popular: (params?: Record<string, any>) => [...queryKeys.prompts.all(), 'popular', params] as const,
    trending: (params?: Record<string, any>) => [...queryKeys.prompts.all(), 'trending', params] as const,
    search: (query: string, params?: Record<string, any>) => [...queryKeys.prompts.all(), 'search', query, params] as const,
    byUser: (userId: string, params?: Record<string, any>) => [...queryKeys.prompts.all(), 'user', userId, params] as const,
    byCategory: (type: string, id: number, params?: Record<string, any>) => [...queryKeys.prompts.all(), 'category', type, id, params] as const,
  },
  
  // Social interactions
  social: {
    all: () => ['social'] as const,
    interactionStatus: (promptId: string) => [...queryKeys.social.all(), 'interaction', promptId] as const,
    saves: (params?: Record<string, any>) => [...queryKeys.social.all(), 'saves', params] as const,
  },
  
  // Comments
  comments: {
    all: () => ['comments'] as const,
    prompt: (promptId: string) => [...queryKeys.comments.all(), 'prompt', promptId] as const,
    replies: (commentId: number) => [...queryKeys.comments.all(), 'replies', commentId] as const,
    recent: (params?: Record<string, any>) => [...queryKeys.comments.all(), 'recent', params] as const,
  },
  
  // Categories
  categories: {
    all: () => ['categories'] as const,
    groups: () => [...queryKeys.categories.all(), 'groups'] as const,
    byType: (type: string) => [...queryKeys.categories.all(), 'type', type] as const,
  },

  // Reports
  reports: {
    all: () => ['reports'] as const,
    reasons: () => [...queryKeys.reports.all(), 'reasons'] as const,
  },
  
  // User data
  user: {
    all: () => ['user'] as const,
    prompts: (params?: Record<string, any>) => [...queryKeys.user.all(), 'prompts', params] as const,
    mostLiked: (params?: Record<string, any>) => [...queryKeys.user.all(), 'prompts', 'most-liked', params] as const,
    saves: (params?: Record<string, any>) => [...queryKeys.user.all(), 'saves', params] as const,
    comments: (params?: Record<string, any>) => [...queryKeys.user.all(), 'comments', params] as const,
    stats: () => [...queryKeys.user.all(), 'stats'] as const,
  },
  
  // Chat
  chat: {
    all: () => ['chat'] as const,
    groups: () => [...queryKeys.chat.all(), 'groups'] as const,
    group: (groupId: string) => [...queryKeys.chat.all(), 'group', groupId] as const,
    messages: (groupId: string) => [...queryKeys.chat.all(), 'messages', groupId] as const,
  },

  // Admin
  admin: {
    all: () => ['admin'] as const,
    users: (params?: Record<string, any>) => [...queryKeys.admin.all(), 'users', params] as const,
    userDetail: (userId: number) => [...queryKeys.admin.all(), 'users', 'detail', userId] as const,
    prompts: (params?: Record<string, any>) => [...queryKeys.admin.all(), 'prompts', params] as const,
    promptDetail: (publicId: string) => [...queryKeys.admin.all(), 'prompts', 'detail', publicId] as const,
    promptCounts: (publicId: string) => [...queryKeys.admin.all(), 'prompts', 'counts', publicId] as const,
    reports: (params?: Record<string, any>) => [...queryKeys.admin.all(), 'reports', params] as const,
    reportsAggregated: (params?: Record<string, any>) => [...queryKeys.admin.all(), 'reports', 'aggregated', params] as const,
    reportDetail: (reportId: number) => [...queryKeys.admin.all(), 'reports', 'detail', reportId] as const,
    promptReports: (promptId: string) => [...queryKeys.admin.all(), 'reports', 'prompt', promptId] as const,
    categories: (params?: Record<string, any>) => [...queryKeys.admin.all(), 'categories', params] as const,
    categoriesPending: () => [...queryKeys.admin.all(), 'categories', 'pending'] as const,
    logs: (params?: Record<string, any>) => [...queryKeys.admin.all(), 'logs', params] as const,
    stats: () => [...queryKeys.admin.all(), 'stats'] as const,
    cachePresets: () => [...queryKeys.admin.all(), 'cache', 'presets'] as const,
    premiumPhotos: (params?: Record<string, any>) => [...queryKeys.admin.all(), 'premium-photos', params] as const,
    premiumVideos: (params?: Record<string, any>) => [...queryKeys.admin.all(), 'premium-videos', params] as const,
  },
} as const;

/**
 * Optimistic update helpers
 */
export const optimisticUpdates = {
  /**
   * Optimistic like/unlike update
   */
  toggleLike: (queryClient: QueryClient, promptId: string, isLiked: boolean) => {
    // Update interaction status
    queryClient.setQueryData(
      queryKeys.social.interactionStatus(promptId),
      (old: any) => ({
        ...old,
        is_liked: !isLiked,
        likes_count: old?.likes_count + (isLiked ? -1 : 1),
      })
    );
    
    // Update prompt detail if cached
    queryClient.setQueryData(
      queryKeys.prompts.detail(promptId),
      (old: any) => ({
        ...old,
        data: {
          ...old?.data,
          prompt: {
            ...old?.data?.prompt,
            likes_count: old?.data?.prompt?.likes_count + (isLiked ? -1 : 1),
          },
        },
      })
    );
  },
  
  /**
   * Optimistic save/unsave update
   */
  toggleSave: (queryClient: QueryClient, promptId: string, isSaved: boolean) => {
    // Update interaction status
    queryClient.setQueryData(
      queryKeys.social.interactionStatus(promptId),
      (old: any) => ({
        ...old,
        is_saved: !isSaved,
        saves_count: old?.saves_count + (isSaved ? -1 : 1),
      })
    );
    
    // Update prompt detail if cached
    queryClient.setQueryData(
      queryKeys.prompts.detail(promptId),
      (old: any) => ({
        ...old,
        data: {
          ...old?.data,
          prompt: {
            ...old?.data?.prompt,
            saves_count: old?.data?.prompt?.saves_count + (isSaved ? -1 : 1),
          },
        },
      })
    );
    
    // Invalidate saved prompts list
    if (isSaved) {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.saves() });
    }
  },
  
  /**
   * Optimistic comment add
   */
  addComment: (queryClient: QueryClient, promptId: string, comment: any) => {
    // Update comments list
    queryClient.setQueryData(
      queryKeys.comments.prompt(promptId),
      (old: any) => ({
        ...old,
        data: {
          ...old?.data,
          data: [comment, ...(old?.data?.data || [])],
          pagination: {
            ...old?.data?.pagination,
            total: (old?.data?.pagination?.total || 0) + 1,
          },
        },
      })
    );
    
    // Update prompt detail comments count
    queryClient.setQueryData(
      queryKeys.prompts.detail(promptId),
      (old: any) => ({
        ...old,
        data: {
          ...old?.data,
          prompt: {
            ...old?.data?.prompt,
            comments_count: (old?.data?.prompt?.comments_count || 0) + 1,
          },
        },
      })
    );
  },
};

/**
 * Cache invalidation helpers
 */
export const invalidateQueries = {
  /**
   * Invalidate all prompt-related queries
   */
  prompts: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.prompts.all() });
  },
  
  /**
   * Invalidate specific prompt detail
   */
  promptDetail: (queryClient: QueryClient, promptId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.prompts.detail(promptId) });
  },
  
  /**
   * Invalidate user-related queries
   */
  user: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.user.all() });
  },
  
  /**
   * Invalidate social interaction queries
   */
  social: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.social.all() });
  },
};

// Export the configured query client instance
export const queryClient = createQueryClient();

export default queryClient;